// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ClickablePresentationProps } from "@insite/mobius/Clickable";
import DataTableContext from "@insite/mobius/DataTable/DataTableContext";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { IconPresentationProps } from "@insite/mobius/Icon";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import applyPropBuilder from "@insite/mobius/utilities/applyPropBuilder";
import get from "@insite/mobius/utilities/get";
import InjectableCss, { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import injectCss from "@insite/mobius/utilities/injectCss";
import MobiusStyledComponentProps from "@insite/mobius/utilities/MobiusStyledComponentProps";
import { color, styledScrollbar } from "@tps/XDL/Utilities";
import * as React from "react";
import styled, { css as csss, ThemeProps, withTheme } from "styled-components";

export enum SortOrderOptions {
    ascending = "ascending",
    descending = "descending",
    none = "none",
    other = "other",
}

export interface DataTableCssOverrides {
    /**
     * @deprecated
     * Use the `css` property on the `DataTable` component instead.
     */
    table?: StyledProp<DataTableProps>;
    body?: StyledProp<DataTableProps>;
    cell?: StyledProp<DataTableProps>;
    head?: StyledProp<DataTableProps>;
    header?: StyledProp<DataTableProps>;
    row?: StyledProp<DataTableProps>;
    evenRow?: StyledProp<DataTableProps>;
    // XNG-Changes
    wrapper?: StyledProp<DataTableProps>;
}

export interface DataTablePresentationProps {
    /** CSS string or styled-components function to be injected into component and children.
     * @themable */
    cssOverrides?: DataTableCssOverrides;
    /** CSS string or styled-components function to be injected into this component. */
    css?: StyledProp<DataTableProps>;
    /**
     * Indicates how the `css` property is combined with the default `css` property from the theme.
     * If true, the default css is applied first and then the component css is applied after causing
     * a merge, much like normal CSS. If false, only the component css is applied, overriding the default css in the theme.
     */
    mergeCss?: boolean;
    /** Props that will be passed to the typography within the header cells.
     * @themable */
    headerTypographyProps?: TypographyPresentationProps;
    /** Props that will be passed to the typography within body cells.
     * @themable */
    cellTypographyProps?: TypographyPresentationProps;
    /** Props that will be passed to the clickable component within sortable header cells.
     * @themable */
    sortClickableProps?: ClickablePresentationProps;
    /** Props to be passed to the sort icon.
     * @themable */
    sortIconProps?: IconPresentationProps;
    /** Source for icons based on sortable or sorted value.
     * @themable */
    sortIconSources?: {
        sortable?: React.ComponentType | string;
        ascending?: React.ComponentType | string;
        descending?: React.ComponentType | string;
        none?: React.ComponentType | string;
        other?: React.ComponentType | string;
    };
}

export type DataTableProps = MobiusStyledComponentProps<
    "table",
    DataTablePresentationProps & {
        /** The list of sort options and order in which they will be applied. */
        sortOrder?: SortOrderOptions[];
    }
>;

const DataTableStyle = styled.table`
    width: 100%;
    border-collapse: collapse;
    position: relative;
    ${injectCss}
`;

// XNG-Changes: add linear-gradient when table is scrollable.
type WrapperProps = {
    isScrollable: boolean;
} & ThemeProps<BaseTheme> &
    InjectableCss;

const Wrapper = styled.div<WrapperProps>`
    overflow-x: auto;
    width: 100%;
    ${({ isScrollable, theme }) =>
        isScrollable &&
        `
        &::after {
            content: "";
            position: absolute;
            right: 0;
            top: 0;
            bottom: 0;
            width: 20px;
            pointer-events: none;
            background: linear-gradient(90deg, rgba(255, 255, 255, 0) 0%, ${get(theme, "colors.common.ui1")} 100%);
        }
    `}
    ${injectCss}
    ${styledScrollbar(4)}
`;

const DataTable: React.FC<DataTableProps> = ({ sortOrder, mergeCss, css, ...otherProps }) => {
    const { applyStyledProp, spreadProps } = applyPropBuilder(
        { ...otherProps, css: otherProps.cssOverrides?.table || css },
        { component: "dataTable" },
    );
    const { theme } = otherProps;
    const cssOverrides = spreadProps("cssOverrides");
    const headerTypographyProps = spreadProps("headerTypographyProps");
    const cellTypographyProps = spreadProps("cellTypographyProps");
    const sortIconProps = spreadProps("sortIconProps");
    const sortIconSources = spreadProps("sortIconSources");
    const sortClickableProps = spreadProps("sortClickableProps");
    const resolvedMergeCss = mergeCss ?? theme?.dataTable.defaultProps?.mergeCss;
    const wrapperRef = React.useRef<any>(null);
    const [isScrollable, setIsScrollable] = React.useState(false);

    React.useEffect(() => {
        const wrapperElement = wrapperRef.current;
        if (!wrapperElement) {
            return;
        }
        const observer = new (window as any).ResizeObserver(([entry]: any) =>
            setIsScrollable(entry.target.scrollWidth > entry.target.clientWidth),
        );
        observer.observe(wrapperElement);
        wrapperElement.addEventListener("scroll", handleScroll);

        return () => {
            observer.disconnect();
            wrapperElement.removeEventListener("scroll", handleScroll);
        };
    }, []);

    const handleScroll = (event: any) => {
        const element = event.target;

        if (
            element.scrollWidth > element.clientWidth &&
            element.scrollLeft + 1 >= element.scrollWidth - element.clientWidth
        ) {
            setIsScrollable(false);
        } else {
            setIsScrollable(true);
        }
    };

    return (
        <DataTableContext.Provider
            value={{
                _cssOverrides: cssOverrides,
                cellTypographyProps,
                headerTypographyProps,
                sortClickableProps,
                sortIconProps,
                sortIconSources,
                sortOrder,
            }}
        >
            {/** XNG: Wrapper to add overlay on small screens */}
            <StyledWrapper
                css={csss`
                    position: relative;
                    width: 100%;
                `}
            >
                <Wrapper
                    ref={wrapperRef}
                    isScrollable={isScrollable}
                    css={applyStyledProp(["cssOverrides", "wrapper"], resolvedMergeCss)}
                >
                    <DataTableStyle {...otherProps} css={applyStyledProp("css", resolvedMergeCss)} />
                </Wrapper>
            </StyledWrapper>
        </DataTableContext.Provider>
    );
};

/** @component */
export default withTheme(DataTable);
